import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { useT } from '@/i18n/LocaleProvider';

/**
 * I-1332 (Jay, 2026-10-09: "dont waste space by creating a big mostly empty lead column"): a row
 * that folds draws its chevron **in its icon's place**, not in a column of its own in front of it,
 * so a row that folds and a row that does not start their icon and title on one line.
 *
 * The icon and the chevron share one box. Which one is drawn is `index.css`'s `.nav-fold` rules, not
 * state here: with a fine pointer the chevron replaces the icon while the row (the nearest
 * `.nav-fold` ancestor) is hovered or holds keyboard focus; with a coarse pointer, where nothing
 * hovers, the chevron is drawn at rest. Under a coarse pointer the box's press area widens to 44 ×
 * 44 around the icon, without moving the title beside it.
 *
 * It sits inside the row's link, so it is `aria-hidden` and out of the tab order, as the caret it
 * replaced was: the keyboard folds the row with → and ←, and the treeitem's `aria-expanded` says
 * whether it is open. With `onToggle` a press folds and does not reach the link; without it (a
 * folder, the Views header) the row's own button already folds on any press.
 */
export function NavFoldGlyph({
  icon,
  expanded,
  name,
  sizeClassName,
  onToggle,
  dataAttributes,
}: {
  /** The row's own icon, drawn at rest. */
  icon: ReactNode;
  expanded: boolean;
  /** What the row is called, for the hover title ("Collapse Roadmap"). */
  name: string;
  /** The icon's box (`h-3.5 w-3.5`), which the chevron takes too. */
  sizeClassName: string;
  onToggle?: () => void;
  /** The row's own test hooks (`data-page-caret`, `data-view-caret`). */
  dataAttributes?: Record<`data-${string}`, string>;
}) {
  const t = useT();
  return (
    <span
      {...dataAttributes}
      data-nav-fold-toggle=""
      aria-hidden="true"
      title={t(expanded ? 'sidebar.collapseLine' : 'sidebar.expandLine', { name })}
      onClick={
        onToggle
          ? (event) => {
              // Inside the row's link, but folding is not opening: the press stops here.
              event.preventDefault();
              event.stopPropagation();
              onToggle();
            }
          : undefined
      }
      // As wide as the icon and as tall as the row, so the press area below spans the row.
      className="nav-fold-glyph relative grid shrink-0 cursor-pointer place-items-center self-stretch"
    >
      <span className="nav-fold-icon grid place-items-center [grid-area:1/1]">{icon}</span>
      <ChevronRight
        data-nav-fold-chevron=""
        className={`nav-fold-chevron text-gray-400 [grid-area:1/1] dark:text-gray-500 ${sizeClassName} ${
          expanded ? 'rotate-90' : ''
        }`}
      />
    </span>
  );
}

/**
 * I-1336: the same icon-and-chevron box as a real button, for a row whose title is a link to a page
 * of its own and whose fold the keyboard has no tree to reach (the All work areas "Views" group, a
 * work area under it). It is focusable, says `aria-expanded`, and names what it folds. It stands in
 * the row's flow exactly where the icon would, and its 44 px press area under a coarse pointer is
 * the same `::before` the glyph has, so the title beside it does not move.
 */
export function NavFoldButton({
  icon,
  expanded,
  name,
  sizeClassName,
  controls,
  onToggle,
}: {
  icon: ReactNode;
  expanded: boolean;
  name: string;
  sizeClassName: string;
  /** The id of the group it folds. */
  controls: string;
  onToggle: () => void;
}) {
  const t = useT();
  const label = t(expanded ? 'sidebar.collapseLine' : 'sidebar.expandLine', { name });
  return (
    <button
      type="button"
      data-nav-fold-toggle=""
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={label}
      title={label}
      onClick={onToggle}
      className="nav-fold-glyph relative grid shrink-0 cursor-pointer place-items-center self-stretch rounded outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <span className="nav-fold-icon grid place-items-center [grid-area:1/1]">{icon}</span>
      <ChevronRight
        data-nav-fold-chevron=""
        className={`nav-fold-chevron text-gray-400 [grid-area:1/1] dark:text-gray-500 ${sizeClassName} ${
          expanded ? 'rotate-90' : ''
        }`}
      />
    </button>
  );
}
